/**
 * The bluerate.ai page's color math, for the page editor's sample buttons
 * and contrast hint. A copy of web/marketplace/src/utils/page-branding.ts
 * (brandPalette, contrastRatio): brandColors.test.ts holds both to the same
 * table, so the editor shows the colors the page will use.
 */

export interface BrandPalette {
  /** The loan officer's color, #RRGGBB upper-case. */
  primary: string;
  /** Text on the primary color: white or near-black, whichever reads better. */
  onPrimary: string;
  /** Hover and pressed states: the primary with 12% black. */
  hover: string;
  /** Light backgrounds of selected items: the primary with 90% white. */
  tint: string;
  /** Light borders next to the tint: the primary with 75% white. */
  soft: string;
}

/** Bluerate's own blue: the page's color when the loan officer picks none. */
export const DEFAULT_BRAND_COLOR = "#0171E4";

export const ON_PRIMARY_LIGHT = "#FFFFFF";
export const ON_PRIMARY_DARK = "#1D1D1F";

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

function toRGB(hex: string): number[] {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

function toHex(rgb: number[]): string {
  return `#${rgb
    .map((c) => Math.round(c).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;
}

function mix(color: number[], other: number[], otherWeight: number): number[] {
  return color.map((c, i) => c * (1 - otherWeight) + other[i] * otherWeight);
}

/** Relative luminance as the Web Content Accessibility Guidelines define it. */
function luminance(rgb: number[]): number {
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function isBrandColor(color: unknown): color is string {
  return typeof color === "string" && HEX_COLOR.test(color);
}

/** Contrast ratio between two #RRGGBB colors, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(toRGB(a)), luminance(toRGB(b))].sort(
    (x, y) => y - x,
  );
  return (hi + 0.05) / (lo + 0.05);
}

/** The palette for a #RRGGBB color, or null for anything else. */
export function brandPalette(color: unknown): BrandPalette | null {
  if (!isBrandColor(color)) {
    return null;
  }
  const primary = color.toUpperCase();
  const rgb = toRGB(primary);
  const onPrimary =
    contrastRatio(primary, ON_PRIMARY_LIGHT) >=
    contrastRatio(primary, ON_PRIMARY_DARK)
      ? ON_PRIMARY_LIGHT
      : ON_PRIMARY_DARK;
  return {
    primary,
    onPrimary,
    hover: toHex(mix(rgb, [0, 0, 0], 0.12)),
    tint: toHex(mix(rgb, [255, 255, 255], 0.9)),
    soft: toHex(mix(rgb, [255, 255, 255], 0.75)),
  };
}
